import React, { useState, useEffect, useRef } from "react"
import { useSelector, useDispatch } from "react-redux"
import { FieldArray, Formik, Form } from "formik"
import { Button, Stack, Table, TableHead, TableBody, TableCell, TableRow, Typography } from "@mui/material"
import dayjs from "dayjs"

import PackagingExecutionHeader from "./PackagingExecutionHeader"
import FormikErrorMessage from "../../components/FormikErrorMessage"
import HoursMaskedField from "../../components/form/MaskedField"
import PEPackagingLineSelect from "./PEPackagingLineSelect"
import PackagingParticularity from "./PackagingParticularity"
import FilterRowSelectAndAutocomplete from "../../components/form/FilterRowSelectAndAutocomplete"
import { getPackagingExecutionSiteSelector, getPackagingExecutionDateSelector, getPackagingExecutionsSelector } from "../../reducers/Packaging/packagingExecution"
import { showPackagingExecutionRead, updatePackagingExecutions } from "../../actions/PackagingExecution/packagingExecution"
import { formatPackagingExecutionsInitialValues, PE_STATUSES_AND_LABELS, getPEsHoursEditUniqueOptions, applyPEsHoursEditFilters, getPEsHoursEditLineOptions } from "../../utils/packagingExecutions"
import { packagingExecutionsValidationSchema } from "../../utils/yupValidators"
import ExecutionsStatus from "../ProductionStepExecutions/ExecutionsStatus"

const tableHeaders = [
  { key: "theoreticalTime", label: "Heures théoriques" },
  { key: "recipe", label: "Recette" },
  { key: "packagingLine", label: "Ligne de barquettage" },
  { key: "sectionsCount", label: "Nb de sections" },
  { key: "expectedPackagingNumber", label: "Nb théorique de produits barquettés" },
  { key: "particularity", label: "Particularité" },
  { key: "status", label: "État du barquettage" }
]

const filterFields = [
  { key: "recipe", placeholder: "Recette concernée" },
  { key: "packagingLine", placeholder: "Ligne de barquettage", isPackagingLine: true },
  { key: "sectionsCount", placeholder: "Nb de sections" },
  { key: "particularity", placeholder: "Particularité" },
  { key: "status", placeholder: "État du barquettage", isStatus: true },
]

// Create consistent horizontal padding on both sides of the table
const BorderCell = () => <TableCell style={{ width: "56px" }} />

const PackagingExecutionHoursEdit = () => {
	const dispatch = useDispatch()
  const formRef = useRef(null)

	const selectedSite = useSelector(getPackagingExecutionSiteSelector)
	const selectedDate = useSelector(getPackagingExecutionDateSelector)
	const packagingExecutions = useSelector(getPackagingExecutionsSelector)

	const [packagingExecutionsInitialValues, setPackagingExecutionsInitialValues] = useState([])
	const [filters, setFilters] = useState({})

	const goToPackagingExecutionRead = () => {
		dispatch(showPackagingExecutionRead(selectedSite.objectId, selectedDate))
	}

	useEffect(() => {
		const initialValues = formatPackagingExecutionsInitialValues(packagingExecutions)
		setPackagingExecutionsInitialValues(initialValues)
	}, [packagingExecutions])

	const _renderStatus = ({ status }) => {
		const statusValue = PE_STATUSES_AND_LABELS.find(object => object.label === status)?.key
		return <ExecutionsStatus statusValue={statusValue} statusName={status} />
	}

	const handleSubmit = async (values) => {
		if (!values.packagingExecutions) return
    await dispatch(updatePackagingExecutions(values.packagingExecutions))
		dispatch(showPackagingExecutionRead(selectedSite.objectId, selectedDate))
	}

	return (
		<>
			<PackagingExecutionHeader
				subtitle={`${selectedSite.name} - ${dayjs(selectedDate).format("DD/MM/YYYY")}`}
				rightAction={
					<Stack direction="row" spacing={2}>
						<Button
							variant="outlined"
							onClick={goToPackagingExecutionRead}
						>
							ANNULER
						</Button>
						<Button
							variant="contained"
							color="primary"
							onClick={() => formRef.current?.submitForm()}
						>
							ENREGISTRER
						</Button>
					</Stack>
				}
			/>
			<Formik
				innerRef={formRef}
				initialValues={packagingExecutionsInitialValues}
				validationSchema={packagingExecutionsValidationSchema}
				initialTouched={{}}
				enableReinitialize
				validateOnBlur={false}
				onSubmit={handleSubmit}
			>
				{({ values, touched, setFieldValue }) => (
					<Form>
						<Table sx={{ tableLayout: "fixed" }}>
							<TableHead>
								<BorderCell />
								{tableHeaders.map(({ key, label }) => <TableCell key={key}>{label}</TableCell>)}
								<BorderCell />
                <FilterRowSelectAndAutocomplete
                  tableHeaders={tableHeaders}
                  filterFields={filterFields}
                  filters={filters}
                  setFilters={setFilters}
                  values={values}
                  site={selectedSite}
                  getUniqueOptions={(values, key, isPackagingLine) =>
                    getPEsHoursEditUniqueOptions(values, key, selectedSite, isPackagingLine)
                  }
                />
              </TableHead>

              <TableBody>
                <FieldArray name="packagingExecutions">
                  {() => (
                    <>
                      {(applyPEsHoursEditFilters(values, filters, selectedSite, filterFields, getPEsHoursEditLineOptions) || []).map((PE) => {
                        return (
                          <TableRow key={PE.objectId + PE.index}>
                            <BorderCell />
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Stack>
                                  <HoursMaskedField
                                    // use index of the non filtered list to keep track of the original position
                                    name={`packagingExecutions.${PE.index}.theoreticalStartTime`}
                                    value={PE.theoreticalStartTime}
                                    onChange={e => setFieldValue(`packagingExecutions.${PE.index}.theoreticalStartTime`, e.target.value)}
                                  />
                                  {touched?.packagingExecutions?.[PE.index]?.theoreticalStartTime && (
                                    <FormikErrorMessage name={`packagingExecutions.${PE.index}.theoreticalStartTime`} />
                                  )}
                                </Stack>
                                <div>-</div>
                                <Stack>
                                  <HoursMaskedField
                                    name={`packagingExecutions.${PE.index}.theoreticalEndTime`}
                                    value={PE.theoreticalEndTime}
                                    onChange={e => setFieldValue(`packagingExecutions.${PE.index}.theoreticalEndTime`, e.target.value)}
                                  />
                                  {touched?.packagingExecutions?.[PE.index]?.theoreticalEndTime && (
                                    <FormikErrorMessage name={`packagingExecutions.${PE.index}.theoreticalEndTime`} />
                                  )}
                                </Stack>
                              </Stack>
                            </TableCell>
                            <TableCell sx={{ fontSize: 16 }}>
                              {PE.code} - {PE.recipe}
                            </TableCell>
                            <TableCell>
                              <PEPackagingLineSelect
                                name={`packagingExecutions.${PE.index}.packagingLine`}
                                value={PE.packagingLine}
                                onChange={e => setFieldValue(`packagingExecutions.${PE.index}.packagingLine`, e.target.value)}
                                options={getPEsHoursEditLineOptions(selectedSite)}
                              />
                            </TableCell>
                            <TableCell>
                              <Typography sx={{ fontSize: 16 }}>{PE.sectionsCount}</Typography>
                            </TableCell>
                            <TableCell sx={{ fontSize: 16 }}>
                              {PE.expectedPackagingNumber}
                            </TableCell>
                            <TableCell sx={{ fontSize: 16 }}>
                              <PackagingParticularity particularities={PE.particularity} />
                            </TableCell>
                            <TableCell>
                              {_renderStatus(PE)}
                            </TableCell>
                            <BorderCell />
                          </TableRow>
                        )
                      })}
                    </>
                  )}
                </FieldArray>
              </TableBody>
            </Table>
          </Form>
        )}
      </Formik>
    </>
  )
}

export default PackagingExecutionHoursEdit